/** Presentation helpers. No game logic lives here (invariant 6). */

import { gameDayKey } from '@geogame/shared';
import type { RoundOutcome, Tier } from '@geogame/shared';

const GROUPED = new Intl.NumberFormat('en-US');

export function points(n: number): string {
  return GROUPED.format(Math.round(n));
}

export function seconds(ms: number): string {
  return (Math.max(0, ms) / 1000).toFixed(1);
}

/**
 * What to CALL a tier in front of a player.
 *
 * The difficulty word, not the flavour name. Explorer, Navigator and
 * Cartographer say nothing about which is harder -- left-to-right order was
 * doing all that work, and only for somebody who had noticed there was an
 * order. The home screen fixed this for itself years before this function did,
 * with its own private map, and the two then disagreed: the same match was
 * "Easiest" on one screen and "Explorer" on the next.
 *
 * ONE FUNCTION, so there is nowhere for a second answer to live. The internal
 * names stay internal; they are good names for a codebase and bad ones for a
 * menu.
 */
export function tierLabel(tier: Tier): string {
  switch (tier) {
    case 'explorer':
      return 'Easiest';
    case 'navigator':
      return 'Standard';
    case 'cartographer':
      return 'Hard';
    // The clue tier. "Expert" rather than a fourth superlative, because the
    // menu already had a Hardest and the new tier is a different KIND of
    // hard: the same questions, with the country's name taken away.
    case 'surveyor':
      return 'Expert';
  }
}

export function signed(n: number): string {
  return n > 0 ? `+${points(n)}` : points(n);
}

/** Wordle-style cell for the daily share grid. */
export function outcomeEmoji(outcome: RoundOutcome, finalRung: number, ladderDepth: number): string {
  if (outcome === 'banked') return finalRung >= ladderDepth ? '🟩' : '🟨';
  if (outcome === 'busted') return '🟥';
  return '⬛';
}

export function outcomeLabel(outcome: RoundOutcome): string {
  switch (outcome) {
    case 'banked':
      return 'Banked';
    case 'busted':
      return 'Busted';
    case 'timeout':
      return 'Timed out';
    case 'missed':
      return 'Missed';
    default:
      return outcome;
  }
}

/** Local YYYY-MM-DD, which is what the daily challenge is keyed by. */
/**
 * Today, for anything about the daily.
 *
 * DELEGATES, and it did not used to. This read the BROWSER's local date while
 * the gateway read UTC, so the two disagreed by up to a day for most of the
 * planet: a card could say "new today" about a puzzle the server had already spent,
 * or hide one that was waiting. Both now ask `gameDayKey`, which answers in the
 * zone the daily actually turns over in.
 */
export function todayKey(d: Date = new Date()): string {
  return gameDayKey(d);
}

// ---------------------------------------------------------------------------
// The ladder, in the player's words
// ---------------------------------------------------------------------------

/*
 * RUNG IS A CODE WORD, NOT A PLAYER WORD.
 *
 * It is the right name inside the rules package, where rung 1 and rung 5 are
 * the same kind of thing — a step with a multiplier — and it should stay there.
 * On screen it was doing real damage. Playtesters read "Push · rung 2" as an
 * offer to have another go at the globe, because nothing anywhere said the
 * thing they were betting on was a QUESTION, and the one noun that might have
 * told them was a word the game never defined. Two or three matches in, they
 * worked it out. Most players will not spend two or three matches on it.
 *
 * So: rung 1 is the pin, and every rung above it is a numbered question. The
 * arithmetic lives here rather than at eight call sites, because an off-by-one
 * between the button and the reveal would be worse than the jargon.
 *
 * `golden rung` SURVIVES, deliberately. It is a proper noun with its own
 * colour, its own flame and its own animation, nobody was ever confused by it,
 * and "golden question" would throw away recognition we already have.
 */

/** How many QUESTIONS a ladder of this depth holds. Rung 1 is the pin. */
export function questionCount(ladderDepth: number): number {
  return Math.max(0, ladderDepth - 1);
}

/** "the pin" / "question 3". Sentence case, for use mid-sentence. */
export function rungLabel(rung: number): string {
  if (rung <= 1) return 'the pin';
  return `question ${rung - 1}`;
}

/** "Pin" / "Question 3". For a label or a button, where it starts a phrase. */
export function rungTitle(rung: number): string {
  if (rung <= 1) return 'Pin';
  return `Question ${rung - 1}`;
}

/**
 * How far up you got, as a fraction: "pin only", "question 2 of 5".
 *
 * A round that ended on the pin has no question number to quote, and printing
 * "question 0 of 5" for it reads as a failure to answer something rather than
 * as a decision to bank early — which is the most common good outcome there is.
 */
export function rungProgress(rung: number, ladderDepth: number): string {
  const total = questionCount(ladderDepth);
  if (rung <= 1) return 'pin only';
  return `question ${rung - 1} of ${total}`;
}

/**
 * One round, in one line: what happened and how far it got.
 *
 * THE PROGRESS HALF IS DROPPED WHEN THE PIN WAS NEVER CLEARED. "pin only ·
 * Missed" reads as though the pin was the part that went right, when in fact
 * it is the part that went wrong and there was never a question to reach.
 * "Banked · pin only" is worth saying — banking straight off the globe is a
 * real strategy and the reader wants to see it — but the same words after a
 * miss are noise at best and a contradiction at worst.
 *
 * Shared by the round card and the results table so the two cannot drift.
 */
export function roundLine(
  outcome: RoundOutcome,
  finalRung: number,
  ladderDepth: number,
): string {
  const label = outcomeLabel(outcome);
  const reachedAQuestion = finalRung > 1;
  if (!reachedAQuestion && outcome !== 'banked') return label;
  return `${label} · ${rungProgress(finalRung, ladderDepth)}`;
}
